import { z } from "zod";

import { VibeDeploymentScreenshotSchema } from "../../../../schemas/VibeDeployment";
import { VibeFailureReasonCodeReadSchema } from "../../../../shared/domain/vibe/vibe-failure-reason-code";
import { VibeTriggerSourceReadSchema } from "./enum-read.schemas";
import { VibeDeploymentStatusReadSchema } from "./status-read.schemas";

/**
 * Slim summary of one of an app's deployments, embedded in the list read so
 * the console renders per-app state without an N+1 of deployment fetches. The
 * full deployment shape stays on the `/deployments` endpoints.
 *
 * Used for BOTH signals the list carries (`latestDeployment` and
 * `servingDeployment`) — they are two different rows, not two different
 * shapes, and one schema keeps them from drifting.
 */
export const VibeAppDeploymentSummarySchema = z.object({
  id: z.string().uuid(),
  /// User-facing monotonic version (`v{n}`) — what the app card renders.
  versionNumber: z.number().int(),
  status: VibeDeploymentStatusReadSchema,
  triggerSha: z.string(),
  /**
   * What kicked this deployment off — a push to the deploy branch, a CLI
   * `vibe deploy`, or the console. Taken from the AUTH PATH at trigger time,
   * never from a request body, so it is a fact about how the deploy arrived
   * rather than a claim the caller made.
   *
   * There are THREE sources, not one. A surface that renders a hardcoded
   * "git push" here is wrong for every CLI and console deploy, and those are
   * the two a reader is most likely to be asking about when something is
   * surprising.
   *
   * `.optional()` and NOT `.nullable()`: the column is `NOT NULL` with a
   * default, so `null` is not a value it can hold and offering it would invite
   * a consumer to handle a state that cannot exist. Absent is the only extra
   * state, and it means the same thing every optional key on this list read
   * means — see `createdBy` on `VibeAppListItemSchema` for the deploy-skew
   * argument in full: the console (Vercel) and the backend (Porter) ship
   * independently, so a frontend one release AHEAD parses a response that
   * omits this key, and a required key turns that window into a whole-response
   * parse failure that tells an org with apps that it has none.
   */
  triggerSource: VibeTriggerSourceReadSchema.optional(),
  /**
   * The port the BUILD observed this image listening on, or `null` when it
   * observed none — a Nixpacks build infers its own Dockerfile, and every row
   * predating the column is null too.
   *
   * 🔴 **THIS IS NOT THE PORT THE APP IS SERVED ON, AND RENDERING IT AS ONE IS
   * THE MISREAD THIS FIELD INVITES.** The effective port is
   * `vibeAppContainerPort(healthCheckConfig, detectedPort)` — an operator's
   * `healthCheckConfig.port` outranks this, and the platform default is what
   * both falling through lands on. That helper is exported from `@nexus/types`
   * and is the single place the precedence lives; a consumer that reads this
   * column directly has forked it. `healthCheckConfig` is already on the list
   * item, so both of its inputs are in hand here.
   *
   * `.nullish()` rather than `.nullable()` for the deploy-skew reason above,
   * and the collapse is free here: the helper's own signature takes
   * `detectedPort?: number | null`, so "absent because the server predates
   * this field" and "observed nothing" already fall through identically.
   */
  detectedPort: z.number().int().nullish(),
  /// One-liner set when the deployment landed FAILED / ROLLED_BACK.
  errorReason: z.string().nullable(),
  /// The structured code for that failure — the only thing the card may
  /// classify on. See `VibeDeploymentSchema.reasonCode`.
  reasonCode: VibeFailureReasonCodeReadSchema,
  createdAt: z.coerce.date(),
  /**
   * The captured homepage of THIS version, or `null` until the capture sweep
   * reaches it (and forever, for a `PRIVATE` app — one that is header-gated at
   * the edge cannot be photographed, and the console renders its identity plate
   * instead).
   *
   * It rides on the deployment summary rather than on the app because that is
   * the whole point of where the columns live: the image and the version that
   * produced it are the same row, so a stale capture can never imply a fresh
   * deploy. A consumer wanting "the app's screenshot" reads it off
   * `servingDeployment` and gets, structurally, the picture of what is actually
   * answering the URL.
   */
  screenshot: VibeDeploymentScreenshotSchema.nullable()
});

export type VibeAppDeploymentSummaryDto = z.infer<typeof VibeAppDeploymentSummarySchema>;
